import { fillerColDef } from "@/lib/fillerColDef";
import { ColDef } from "ag-grid-community";

// Cell Renderers
import FillPriceCellRenderer from "./cellRenderers/FillPriceCellRenderer";
import FillQuantityCellRenderer from "./cellRenderers/FillQuantityCellRenderer";
import FilledPercentCellRenderer from "./cellRenderers/FilledPercentCellRenderer";
import RemoveFillButtonCellRenderer from "./cellRenderers/RemoveFillButtonCellRenderer";
import EditFillButtonCellRenderer from "./cellRenderers/EditFillButtonCellRenderer";
import FilledAtCellRenderer from "./cellRenderers/FilledAtCellRenderer";

// Cell Editors
import FillPriceCellEditor from "./cellRenderers/FillPriceCellRenderer/FillPriceCellEditor";
import FillQuantityCellEditor from "./cellRenderers/FillQuantityCellRenderer/FillQuantityCellEditor";

// Value Formatters
import { fillPriceValueFormatter } from "./cellRenderers/FillPriceCellRenderer/fillPriceFormatter";
import { fillQuantityValueFormatter } from "./cellRenderers/FillQuantityCellRenderer/fillQuantityFormatter";
import { filledPercentValueFormatter } from "./cellRenderers/FilledPercentCellRenderer/filledPercentFormatter";
import { filledAtValueFormatter } from "./cellRenderers/FilledAtCellRenderer/filledAtFormatter";

// Helpers
import { isFillPriceCellEditable } from "./cellRenderers/FillPriceCellRenderer/fillPriceHelpers";
import { isFillQuantityCellEditable } from "./cellRenderers/FillQuantityCellRenderer/fillQuantityHelpers";
import { TableHeaderField } from "@/lib/interfaces and Enums/ENUMTableHeaders";
import { ColId } from "./ENUMActionSummaryColIds";
import { defaultColumnWidths } from "@/lib/interfaces and Enums/ENUMColumnWidths";
import { statusToShowDealtPrice } from "./colDefs/colDefsShared";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";

/**
 * Columns replicated from the leg/block level to maintain alignment.
 * These columns mirror the parent table structure but display empty cells.
 * When column visibility changes at the leg level, the same changes should apply here.
 */
export const fillsReplicatedColIds = [
  ColId.expand,
  ColId.feeAdjustedQuote,
  ColId.direction,
  ColId.notionalLeg1,
  ColId.security,
  ColId.settlementDate,
  ColId.level,
];

export interface IOrderFillsColDefsParams {
  fullTradeData: any;
  isRP: boolean;
  status: string;
}

/**
 * Returns column definitions for the Order Fills table (NESTED TABLE 3)
 *
 * This table displays individual fill entries for Order trade types.
 * - RP users can add/edit/remove fills
 * - OP users can only view fills
 *
 * @param fullTradeData - The parent trade data object
 * @param isRP - Whether the current user is the RP (Receiving Party)
 */
export function returnOrderFillsColDefs({
  fullTradeData,
  isRP,
  status,
}: IOrderFillsColDefsParams): ColDef[] {
  // Determine if dealt price should be visible based on status
  const showDealtPrice = statusToShowDealtPrice.includes(
    status as ExecutionStrategyStatus,
  );

  // Replicated columns from leg level - empty cells for alignment
  // These mirror the parent table structure so visibility changes propagate automatically
  const replicatedColDefs: ColDef[] = [
    {
      field: ColId.expand,
      colId: ColId.expand,
      headerClass: "hideAgHeader",
      headerName: "",
      width: defaultColumnWidths.expandArrow,
      cellRenderer: () => null, // Empty cell
      hide: false,
    },

    {
      field: ColId.direction,
      colId: ColId.direction,
      headerClass: "hideAgHeader",
      headerName: "",
      width: defaultColumnWidths.side,
      cellRenderer: () => null, // Empty cell
      hide: false,
    },
    {
      field: ColId.notionalLeg1,
      colId: ColId.notionalLeg1,
      headerClass: "hideAgHeader",
      headerName: "",
      width: defaultColumnWidths.nominal,
      cellRenderer: () => null, // Empty cell
      hide: false,
    },
    {
      field: ColId.security,
      colId: ColId.security,
      headerClass: "hideAgHeader",
      headerName: "",
      width: defaultColumnWidths.name,
      // flex: 1,
      cellRenderer: () => null, // Empty cell
      hide: false,
    },
    {
      field: ColId.settlementDate,
      colId: ColId.settlementDate,
      headerClass: "hideAgHeader",
      headerName: "",
      width: defaultColumnWidths.date,
      cellRenderer: () => null, // Empty cell
      hide: false,
    },
    {
      field: ColId.level,
      colId: ColId.level,
      headerClass: "hideAgHeader",
      headerName: "",
      width: defaultColumnWidths.level,
      cellRenderer: () => null, // Empty cell
      hide: false,
    },
  ];

  const baseColDefs: ColDef[] = [
    {
      field: "fillSize",
      headerClass: "hideAgHeader",
      headerName: "Fill Quantity",
      width: defaultColumnWidths.fillQuantity,
      cellRenderer: FillQuantityCellRenderer,
      cellRendererParams: { fullTradeData },
      valueFormatter: fillQuantityValueFormatter,
      editable: isRP ? isFillQuantityCellEditable : false,
      cellEditor: isRP ? FillQuantityCellEditor : undefined,
      cellEditorParams: isRP ? { fullTradeData } : undefined,
      cellClass: isRP ? "ag-roundedInputStyle" : undefined,
      hide: false,
    },
    {
      field: "fillRate",
      headerClass: "hideAgHeader",
      headerName: "Fill Price",
      width: defaultColumnWidths.fillPrice,
      cellRenderer: FillPriceCellRenderer,
      cellRendererParams: { fullTradeData },
      valueFormatter: fillPriceValueFormatter,
      editable: isRP ? isFillPriceCellEditable : false,
      cellEditor: isRP ? FillPriceCellEditor : undefined,
      cellEditorParams: isRP ? { fullTradeData } : undefined,
      cellClass: isRP ? "ag-roundedInputStyle" : undefined,
      hide: false,
    },
    {
      field: ColId.feeAdjustedQuote,
      colId: ColId.feeAdjustedQuote,
      headerClass: "hideAgHeader",
      headerName: "",
      width: defaultColumnWidths.feeAdjustedQuote,
      cellRenderer: () => null, // Empty cell
      hide: !showDealtPrice, // Match leg-level visibility
    },
    {
      field: "filledPercent",
      headerClass: "hideAgHeader",
      headerName: "Filled %",
      width: 100,
      cellRenderer: FilledPercentCellRenderer,
      cellRendererParams: { fullTradeData },
      valueFormatter: filledPercentValueFormatter,
      hide: false,
    },
    {
      field: "filledAt",
      headerName: TableHeaderField.filledAt, // Actioned At
      colId: ColId.filledAt,
      width: defaultColumnWidths.filledAt,
      cellRenderer: FilledAtCellRenderer,
      cellRendererParams: { fullTradeData },
      valueFormatter: filledAtValueFormatter,
      hide: false,
    },
  ];

  // RP-only columns for managing fills
  const rpOnlyColDefs: ColDef[] = isRP
    ? [
        {
          field: "Remove",
          headerName: "",
          headerClass: "hideAgHeader",
          width: 20,
          cellRenderer: RemoveFillButtonCellRenderer,
          cellRendererParams: { fullTradeData },
          hide: false,
        },
        {
          field: "Edit",
          headerName: "",
          headerClass: "hideAgHeader",
          width: 20,
          cellRenderer: EditFillButtonCellRenderer,
          cellRendererParams: { fullTradeData },
          hide: false,
        },
      ]
    : [];

  return [
    ...replicatedColDefs,
    ...baseColDefs,
    ...rpOnlyColDefs,
    ...fillerColDef,
  ];
}

/**
 * Returns column definitions for OP users viewing Order Fills
 * Simplified version without edit/add/remove capabilities
 */
export function returnOrderFillsColDefsOP(
  fullTradeData: any,
  status: string,
): ColDef[] {
  return returnOrderFillsColDefs({ fullTradeData, isRP: false, status });
}

/**
 * Returns column definitions for RP users managing Order Fills
 * Includes edit/add/remove capabilities
 */
export function returnOrderFillsColDefsRP(
  fullTradeData: any,
  status: string,
): ColDef[] {
  return returnOrderFillsColDefs({ fullTradeData, isRP: true, status });
}
